document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const body = document.body;

  const setLocked = (locked) => body.classList.toggle('is-locked', locked);

  const openPanel = (panel, toggle, focusSelector) => {
    if (!panel) return;
    panel.hidden = false;
    toggle?.setAttribute('aria-expanded', 'true');
    setLocked(true);
    window.requestAnimationFrame(() => panel.querySelector(focusSelector)?.focus());
  };

  const closePanel = (panel, toggle) => {
    if (!panel) return;
    panel.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    setLocked(false);
    toggle?.focus();
  };

  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  menuToggle?.addEventListener('click', () => openPanel(mobileMenu, menuToggle, '[data-menu-close]'));
  mobileMenu?.querySelectorAll('[data-menu-close]').forEach((button) => {
    button.addEventListener('click', () => closePanel(mobileMenu, menuToggle));
  });

  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchModal = document.querySelector('[data-search-modal]');
  searchToggle?.addEventListener('click', () => openPanel(searchModal, searchToggle, 'input[type="search"]'));
  searchModal?.querySelectorAll('[data-search-close]').forEach((button) => {
    button.addEventListener('click', () => closePanel(searchModal, searchToggle));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (mobileMenu && !mobileMenu.hidden) closePanel(mobileMenu, menuToggle);
    if (searchModal && !searchModal.hidden) closePanel(searchModal, searchToggle);
    document.querySelectorAll('[data-filter-panel]:not([hidden])').forEach((panel) => {
      panel.hidden = true;
      document.querySelector('[data-filter-toggle]')?.setAttribute('aria-expanded', 'false');
      setLocked(false);
    });
  });

  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-carousel-shell]:not([data-initialized])').forEach((shell) => {
      shell.dataset.initialized = 'true';
      const track = shell.querySelector('[data-carousel]');
      const previous = shell.querySelector('[data-carousel-prev]');
      const next = shell.querySelector('[data-carousel-next]');
      if (!track || !previous || !next) return;

      const getDistance = () => {
        const firstCard = track.firstElementChild;
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || '0');
        return firstCard ? firstCard.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      };

      const updateControls = () => {
        const maxScroll = track.scrollWidth - track.clientWidth - 2;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll;
      };

      previous.addEventListener('click', () => track.scrollBy({ left: -getDistance(), behavior: reducedMotion ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: getDistance(), behavior: reducedMotion ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
    });
  };

  const initQuantities = (root = document) => {
    root.querySelectorAll('[data-quantity-selector]:not([data-initialized])').forEach((selector) => {
      selector.dataset.initialized = 'true';
      const input = selector.querySelector('input[type="number"]');
      if (!input) return;
      selector.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const minimum = Number.parseInt(input.min || '0', 10);
        input.value = String(Math.max(minimum, Number.parseInt(input.value || '0', 10) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      selector.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const maximum = input.max ? Number.parseInt(input.max, 10) : Number.POSITIVE_INFINITY;
        input.value = String(Math.min(maximum, Number.parseInt(input.value || '0', 10) + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initProducts = (root = document) => {
    root.querySelectorAll('[data-product-section]:not([data-initialized])').forEach((section) => {
      section.dataset.initialized = 'true';
      const select = section.querySelector('[data-variant-select]');
      if (!select) return;

      const currentPrice = section.querySelector('[data-current-price]');
      const comparePrice = section.querySelector('[data-compare-price]');
      const availabilityText = section.querySelector('[data-availability-text]');
      const availabilityDot = section.querySelector('.availability-dot');
      const addButton = section.querySelector('[data-add-to-cart]');
      const addText = section.querySelector('[data-add-to-cart-text]');

      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const available = option.dataset.available === 'true';
        if (currentPrice) currentPrice.textContent = option.dataset.price || '';
        if (comparePrice) {
          comparePrice.textContent = option.dataset.comparePrice || '';
          comparePrice.hidden = !option.dataset.comparePrice;
        }
        if (availabilityText) availabilityText.textContent = available ? section.dataset.inStockLabel : section.dataset.outOfStockLabel;
        availabilityDot?.classList.toggle('availability-dot--empty', !available);
        if (addButton) addButton.disabled = !available;
        if (addText) addText.textContent = available ? section.dataset.addLabel : section.dataset.soldLabel;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url.toString());
      });
    });
  };

  const initProductGalleries = (root = document) => {
    root.querySelectorAll('[data-product-gallery-shell]:not([data-initialized])').forEach((shell) => {
      shell.dataset.initialized = 'true';
      const gallery = shell.querySelector('[data-product-gallery]');
      const items = Array.from(shell.querySelectorAll('[data-product-gallery-item]'));
      const previous = shell.querySelector('[data-product-gallery-previous]');
      const next = shell.querySelector('[data-product-gallery-next]');
      const current = shell.querySelector('[data-product-gallery-current]');
      if (!gallery || items.length < 2 || !previous || !next) return;

      let activeIndex = 0;
      let animationFrame;

      const findActiveIndex = () => {
        const galleryLeft = gallery.getBoundingClientRect().left;
        return items.reduce((closestIndex, item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - galleryLeft);
          const closestDistance = Math.abs(items[closestIndex].getBoundingClientRect().left - galleryLeft);
          return distance < closestDistance ? index : closestIndex;
        }, 0);
      };

      const updateControls = () => {
        activeIndex = findActiveIndex();
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex === items.length - 1;
        if (current) current.textContent = String(activeIndex + 1);
      };

      const goTo = (index) => {
        const targetIndex = Math.max(0, Math.min(index, items.length - 1));
        const target = items[targetIndex];
        const left = gallery.scrollLeft + target.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
        gallery.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
      };

      previous.addEventListener('click', () => goTo(activeIndex - 1));
      next.addEventListener('click', () => goTo(activeIndex + 1));
      gallery.addEventListener('scroll', () => {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = window.requestAnimationFrame(updateControls);
      }, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
    });
  };

  const syncUniverseEntryLock = () => {
    const hasActiveOverlay = document.querySelector(
      '[data-universe-entry].is-active.universe-entry--overlay_session, [data-universe-entry].is-active.universe-entry--overlay_always'
    );
    body.classList.toggle('entry-gate-open', Boolean(hasActiveOverlay));
  };

  const initUniverseEntries = (root = document) => {
    const gates = root.matches?.('[data-universe-entry]')
      ? [root]
      : Array.from(root.querySelectorAll?.('[data-universe-entry]:not([data-initialized])') || []);

    gates.forEach((gate) => {
      if (gate.dataset.initialized === 'true') return;
      gate.dataset.initialized = 'true';

      const mode = gate.dataset.entryMode || 'inline';
      const storageKey = gate.dataset.storageKey || `universe-entry:${gate.id}`;
      const isOverlay = mode !== 'inline';
      const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      const skipButton = gate.querySelector('[data-universe-entry-skip]');

      const hasBeenSeen = () => {
        if (mode !== 'overlay_session' || isDesignMode) return false;
        try {
          return window.sessionStorage.getItem(storageKey) === 'seen';
        } catch (error) {
          return false;
        }
      };

      const rememberVisit = () => {
        if (mode !== 'overlay_session') return;
        try {
          window.sessionStorage.setItem(storageKey, 'seen');
        } catch (error) {
          // The entrance remains usable when browser storage is unavailable.
        }
      };

      const closeGate = (remember = true) => {
        if (remember) rememberVisit();
        gate.classList.remove('is-active');
        gate.hidden = true;
        syncUniverseEntryLock();
      };

      const openGate = () => {
        gate.hidden = false;
        gate.dataset.entryReady = 'true';
        gate.classList.add('is-active');
        syncUniverseEntryLock();
      };

      gate.universeEntryOpen = openGate;
      gate.universeEntryClose = closeGate;
      gate.dataset.entryReady = 'true';

      if (hasBeenSeen()) {
        gate.hidden = true;
      } else {
        openGate();
      }

      skipButton?.addEventListener('click', () => closeGate(true));

gate.querySelectorAll('[data-universe-entry-choice]').forEach((choice) => {
  choice.addEventListener('click', () => {
    gate.classList.add('is-leaving');
  }, { once: true });
});

window.addEventListener('pageshow', () => {
  gate.classList.remove('is-leaving');
});

      gate.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !skipButton || !isOverlay) return;
        event.preventDefault();
        closeGate(true);
      });
    });
  };

  const filterToggle = document.querySelector('[data-filter-toggle]');
  const filterPanel = document.querySelector('[data-filter-panel]');
  filterToggle?.addEventListener('click', () => openPanel(filterPanel, filterToggle, '[data-filter-close]'));
  filterPanel?.querySelector('[data-filter-close]')?.addEventListener('click', () => closePanel(filterPanel, filterToggle));

  document.querySelectorAll('[data-auto-submit]').forEach((select) => {
    select.addEventListener('change', () => select.form?.requestSubmit());
  });

  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.requestSubmit());
  });

  const initPreviewNavigation = (root = document) => {
    const currentUrl = new URL(window.location.href);
    const previewThemeId = currentUrl.searchParams.get('preview_theme_id');
    const isPreviewHost = /\.shopifypreview\.com$/i.test(window.location.hostname);
    const isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    const isThemePreview = Boolean(previewThemeId || isPreviewHost || isDesignMode);

    if (!isThemePreview) return;

    const decorateLink = (link) => {
      if (!link || !link.getAttribute('href')) return;

      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.href);
      } catch (error) {
        return;
      }

      if (!['http:', 'https:'].includes(url.protocol) || url.origin !== currentUrl.origin) return;

      if (previewThemeId && !url.searchParams.has('preview_theme_id')) {
        url.searchParams.set('preview_theme_id', previewThemeId);
        ['_fd', 'pb'].forEach((key) => {
          const value = currentUrl.searchParams.get(key);
          if (value !== null && !url.searchParams.has(key)) url.searchParams.set(key, value);
        });
      }

      const normalizedPath = url.pathname.replace(/\/+$/, '');
      if (normalizedPath === '/pages/souvenirs-legendaires') {
        url.searchParams.set('view', 'souvenirs-legendaires');
      }

      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    };

    root.querySelectorAll('a[href]').forEach(decorateLink);
  };

  initCarousels();
  initQuantities();
  initProducts();
  initProductGalleries();
  initUniverseEntries();
  initPreviewNavigation();

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    initPreviewNavigation(link.parentElement || document);
  }, true);

  document.addEventListener('shopify:section:load', (event) => {
    initCarousels(event.target);
    initQuantities(event.target);
    initProducts(event.target);
    initProductGalleries(event.target);
    initUniverseEntries(event.target);
    initPreviewNavigation(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (!window.Shopify?.designMode) return;
    const gate = event.target.matches?.('[data-universe-entry]')
      ? event.target
      : event.target.querySelector?.('[data-universe-entry]');
    gate?.universeEntryOpen?.();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const gate = event.target.matches?.('[data-universe-entry]')
      ? event.target
      : event.target.querySelector?.('[data-universe-entry]');
    if (gate) {
      gate.classList.remove('is-active');
      syncUniverseEntryLock();
    }
  });
  const initPredictiveSearch = () => {
  const input = document.querySelector('#HeaderSearch');
  const results = document.querySelector('#PredictiveSearchResults');

  if (!input || !results) return;

  let debounceTimer;

  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);

    const query = input.value.trim();

    if (query.length < 4) {
      results.hidden = true;
      results.innerHTML = '';
      return;
    }

    debounceTimer = setTimeout(async () => {
      try {
const response = await fetch(
  window.Shopify.routes.root +
  'search/suggest.json?q=' +
  encodeURIComponent(query) +
  '&resources[type]=product&resources[limit]=5&resources[options][fields]=title'
);

        const data = await response.json();
        const products = data?.resources?.results?.products || [];

const filteredProducts = products.filter((product) =>
  product.title.toLowerCase().includes(query.toLowerCase())
);

if (!filteredProducts.length) {
  results.hidden = true;
  results.innerHTML = '';
  return;
}

results.innerHTML = filteredProducts.map((product) => `
  <a class="predictive-search-result" href="${product.url}">
    ${product.image ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` : ''}
    <span>${product.title}</span>
  </a>
`).join('');

results.hidden = false;
      } catch (error) {
        results.hidden = true;
        results.innerHTML = '';
      }
    }, 350);
  });
};

initPredictiveSearch();
})();
/* Agrandissement des images produit — ordinateur seulement */
(() => {
  if (window.productDesktopLightboxReady) return;
  window.productDesktopLightboxReady = true;

  const desktop = window.matchMedia(
    '(min-width: 990px) and (hover: hover) and (pointer: fine)'
  );

  let dialog;
  let enlargedImage;
  let lastImage;
  let pointerStart;
  let moved = false;

  const createLightbox = () => {
    if (dialog) return;

    dialog = document.createElement('dialog');
    dialog.className = 'product-image-lightbox';
    dialog.setAttribute('aria-label', 'Image du produit agrandie');

    dialog.innerHTML = `
      <button
        type="button"
        class="product-image-lightbox__close"
        aria-label="Fermer l’image agrandie"
      >×</button>

      <img class="product-image-lightbox__image" alt="">
    `;

    document.body.appendChild(dialog);

    enlargedImage = dialog.querySelector(
      '.product-image-lightbox__image'
    );

    dialog
      .querySelector('.product-image-lightbox__close')
      .addEventListener('click', () => dialog.close());

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', () => {
      document.body.classList.remove('product-lightbox-open');
      lastImage?.focus();
    });
  };

  document.addEventListener('pointerdown', (event) => {
    if (!desktop.matches || event.button !== 0) return;

    const image = event.target.closest?.(
      '[data-product-gallery] .product-gallery__image'
    );

    if (!image) return;

    pointerStart = {
      x: event.clientX,
      y: event.clientY
    };

    moved = false;
  }, true);

  document.addEventListener('pointermove', (event) => {
    if (!pointerStart) return;

    if (
      Math.hypot(
        event.clientX - pointerStart.x,
        event.clientY - pointerStart.y
      ) > 8
    ) {
      moved = true;
    }
  }, { capture: true, passive: true });

  document.addEventListener('pointerup', () => {
    pointerStart = null;
    setTimeout(() => {
      moved = false;
    }, 0);
  }, true);

  document.addEventListener('pointercancel', () => {
    pointerStart = null;
    moved = false;
  }, true);

  document.addEventListener('click', (event) => {
    if (!desktop.matches || moved) return;

    const image = event.target.closest?.(
      '[data-product-gallery] .product-gallery__image'
    );

    if (!image) return;

    createLightbox();

    lastImage = image;
    enlargedImage.src = image.currentSrc || image.src;
    enlargedImage.alt = image.alt || 'Image du produit';

    document.body.classList.add('product-lightbox-open');
    dialog.showModal();
    dialog.querySelector('.product-image-lightbox__close').focus();
  });
})();